import { Bell, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { formatRelativeDate } from '@/shared/lib/format';
import { useAuth } from '@/features/auth/auth-store';
import {
  useNotifications,
  useMarkNotificationRead,
  useDismissNotification,
  type Notification,
} from '@/features/notifications';

function NotificationRow({ notification }: { notification: Notification }) {
  const markRead = useMarkNotificationRead();
  const dismiss = useDismissNotification();

  const handleOpen = () => {
    if (!notification.is_read) markRead.mutate(notification.id);
  };

  // Two sibling buttons (open = mark read, dismiss): never a button inside a button.
  return (
    <div className="flex gap-1 rounded-lg bg-surface-high transition-colors hover:bg-surface-highest">
      <button
        type="button"
        onClick={handleOpen}
        className="flex min-w-0 flex-1 gap-2.5 rounded-lg p-2.5 text-left"
      >
        <span
          aria-hidden="true"
          className={cn(
            'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
            notification.is_read ? 'bg-transparent' : 'bg-primary',
          )}
        />
        <span className="min-w-0 flex-1 space-y-0.5">
          <span className="block text-[12.5px] font-semibold text-foreground">
            {notification.title}
          </span>
          {notification.body && (
            <span className="block text-[11.5px] leading-snug text-muted-foreground">
              {notification.body}
            </span>
          )}
          <span className="block text-[10.5px] text-outline-faint">
            {formatRelativeDate(notification.created_at)}
          </span>
        </span>
      </button>
      <button
        type="button"
        aria-label="Скрыть уведомление"
        onClick={() => dismiss.mutate(notification.id)}
        className="m-2.5 ml-0 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-highest hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function NotificationBell() {
  const { status } = useAuth();
  const { data } = useNotifications();

  if (status !== 'authed') return null;

  const items = data?.items ?? [];
  const unreadCount = data?.unread_count ?? 0;
  const label = unreadCount > 0 ? `Уведомления, непрочитанных: ${unreadCount}` : 'Уведомления';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-high hover:text-foreground"
        >
          <Bell className="h-[17px] w-[17px]" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive ring-2 ring-[rgba(11,19,38,0.96)]"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[344px] p-3.5">
        <div className="flex items-center gap-2 pb-2.5">
          <p className="db-eyebrow flex-1">Уведомления</p>
          <span className="text-[11.5px] tabular-nums text-outline-faint">{items.length}</span>
        </div>
        <div className="flex max-h-80 flex-col gap-1.5 overflow-y-auto">
          {items.map((n) => (
            <NotificationRow key={n.id} notification={n} />
          ))}
          {items.length === 0 && (
            <p className="px-1 py-2 text-xs text-muted-foreground">Новых уведомлений нет</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
